import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Stack } from '@mui/material';

import { createNewOrderStore } from 'stores/createNewOrderStore';
import { useLocalization } from 'localization';
import { getSaleProductsApi } from 'api/products/query';
import { ProductStructure } from 'constants/enums/products';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { IProductDto } from 'api/products/dto';
import { CurrencyCode } from 'constants/enums/general';
import useNewOrderCalculatePrice from 'hooks/general/useNewOrderCalculatePrice';
import { transformOrderCreationDataV2 } from 'helpers/transformOrderCreationData';
import { createNewOrderMutationV2 } from 'api/orders/query';
import { NumberInputActionField } from 'components/v2/input/NumberInputActionField';
import { userStore } from 'stores/userStore';

type Group = Record<
  string,
  {
    categoryId: string;
    displayCategory: string;
    rows: IProductDto[];
    displayCategoryOrder?: number;
  }
>;

export const useCreateNewOrderStep4 = () => {
  const { t, f } = useLocalization();
  const { nextStep, formData, setFormData } = createNewOrderStore();
  const { user } = userStore();

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const singleProductsForm = useWatch({ name: 'singleProducts' }) || {};

  const isProductError = Object.keys(errors).some(key => ['singleProducts'].includes(key));

  const { data, isLoading: loadingSaleProducts } = getSaleProductsApi({
    branchId: formData?.branchId,
    pageNumber: 3,
  });

  const { mutateAsync, isLoading: isCreatingOrder } = createNewOrderMutationV2({
    handleSuccess: () => nextStep(),
  });

  const handleCreateNewOrder = async () => {
    const latestFormData = {
      ...formData,
      singleProducts: singleProductsForm,
    };

    setFormData(latestFormData);

    const orderPayload = transformOrderCreationDataV2(latestFormData);
    const response = await mutateAsync(orderPayload);

    setFormData({
      ...latestFormData,
      orderId: response?.data?.data?.orderId ?? '',
    });
  };

  const groupedSingleProducts = useMemo(() => {
    if (!data) return {};

    const map: Group = {};

    for (const item of data) {
      if (item.productStructure !== ProductStructure.SingleProduct) continue;

      const categoryId = item.categoryId ?? '';
      const displayCategory = item.displayCategory ?? '';
      const displayCategoryOrder = item.displayCategoryOrder ?? 9999;

      if (!map[categoryId]) {
        map[categoryId] = {
          categoryId,
          displayCategory,
          displayCategoryOrder,
          rows: [],
        };
      }

      map[categoryId].rows.push(item);
    }

    //Sort products inside each group alphabetically
    for (const group of Object.values(map)) {
      group.rows.sort((a, b) => a.name.localeCompare(b.name));
    }

    // Sort groups by displayCategoryOrder
    const sortedEntries = Object.entries(map).sort(
      ([, a], [, b]) => (a.displayCategoryOrder ?? 9999) - (b.displayCategoryOrder ?? 9999)
    );

    // Convert back to an ordered object
    return Object.fromEntries(sortedEntries);
  }, [data]);

  const getRowIndex = (params: GridRenderCellParams) => {
    return params.api.getSortedRowIds().indexOf(params.id);
  };

  const handleUpdate = useCallback(
    (categoryId: string, index: number, row: IProductDto, value: number) => {
      setValue(`singleProducts.${categoryId}.${index}`, {
        ...row,
        quantity: value,
      });
    },
    [setValue]
  );

  const buildColumnsForCategory = useCallback(
    (categoryId: string) => {
      const columns: GridColDef<IProductDto>[] = [
        {
          headerName: t('orders.pages.new.flowSelection.productName'),
          field: 'name',
          minWidth: 350,
          sortable: false,
          flex: 1,
        },
        {
          headerName: t('common.terms.actions'),
          field: 'id',
          width: 300,
          sortable: false,
          renderCell: (params: GridRenderCellParams<IProductDto>) => {
            const { row } = params;
            const index = getRowIndex(params);
            return (
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                height="100%"
                sx={{
                  '& small': {
                    display: 'none',
                  },
                }}
              >
                <NumberInputActionField
                  control={control}
                  name={`singleProducts.${categoryId}.${index}.quantity`}
                  min={0}
                  onValueChange={value => handleUpdate(categoryId, index, row, value ?? 0)}
                />
              </Stack>
            );
          },
        },
      ];
      if (user?.visiblePrices)
        columns.splice(1, 0, {
          headerName: t('common.terms.price'),
          field: 'discountedPrice',
          width: 300,
          sortable: false,
          renderCell: (params: GridRenderCellParams<IProductDto, number>) =>
            f(params.value || 0, { currency: CurrencyCode.DKK, decimals: 2 }),
        });
      return columns;
    },
    [t, f, control, handleUpdate]
  );

  const totalPrice = useNewOrderCalculatePrice();

  const isDisableCreateOrder =
    isProductError || loadingSaleProducts || totalPrice === f(0, { currency: CurrencyCode.DKK, decimals: 2 });

  return {
    t,
    ui: { table: { columns: { buildColumns: buildColumnsForCategory } } },
    groupedSingleProducts,
    formData,
    totalPrice,
    isDisableCreateOrder,
    handleCreateNewOrder,
    apiState: { isCreatingOrder, loadingSaleProducts, showPrice: user?.visiblePrices },
  };
};
